"use client";

import * as React from "react";

import { cn } from "./utils";

/** White checkmark (transparent bg in SVG) over checked fill — see frontend-designer `reference/omni-app/components.md` secondary button. */
const CHECKED_MARK_BG: string = (() => {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
})();

const baseClassName =
  "h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-lg border-2 transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff] focus-visible:ring-offset-2 " +
  "disabled:cursor-not-allowed disabled:opacity-50";

export type OmniCheckboxProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "type" | "defaultChecked"
> & {
  checked: boolean;
  /** Called with the new checked value after user interaction. */
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * Beatly Omni–styled native checkbox: visible unchecked frame, secondary purple (#2e024c) when checked.
 * Use for tables, forms, and settings where browser `accent-color` is not acceptable.
 */
export const OmniCheckbox = React.forwardRef<HTMLInputElement, OmniCheckboxProps>(
  ({ className, checked, disabled, onCheckedChange, onChange, style, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange?.(e);
          onCheckedChange?.(e.target.checked);
        }}
        className={cn(
          baseClassName,
          checked
            ? "border-[#2e024c] bg-[#2e024c] hover:border-[#3d0661] hover:bg-[#3d0661]"
            : "border-[#667085] bg-white hover:border-[#344054]",
          className,
        )}
        style={
          checked
            ? {
                backgroundImage: CHECKED_MARK_BG,
                backgroundSize: "14px 14px",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                ...style,
              }
            : style
        }
        {...props}
      />
    );
  },
);

OmniCheckbox.displayName = "OmniCheckbox";
